import { useAuth } from "@cheatsheetz/sheetz-core/providers/AuthProvider";
import { useApiFetch } from "@cheatsheetz/sheetz-core/providers/CheatsheetzFetchProvider";
import type {
  RoleDto,
  SubjectDto,
  SubjectEntitlementsDto,
  SubjectSubscriptionDto,
} from "@cheatsheetz/sheetz-core/types";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { entitlementsQueryKeys } from "../../hooks/useEntitlements";
import { AccountRoutes } from "../../routes";
import type { UserInfo } from "../../types";

export interface SessionContextValue {
  /** The currently authenticated user, or null if not logged in. */
  user: UserInfo | null;
  /** Whether the session state is being determined (e.g. on initial load). */
  isLoading: boolean;
  /** Whether a user is currently authenticated. */
  isAuthenticated: boolean;
  /** Whether the user needs to complete the Getting Started provisioning flow. */
  needsProvisioning: boolean;
  /** Redirect to login (OIDC authorization). */
  login: (returnUrl?: string) => Promise<void>;
  /** Log out and clear the user. */
  logout: () => Promise<void>;
  /** Update local user state (e.g. after login / registration / profile changes). */
  setUser: (user: UserInfo | null) => void;
  entitlements: SubjectDto[];
  /** All element-level role definitions with their actions. */
  elementRoles: RoleDto[];
  /** The user's active subscriptions. */
  subscriptions: SubjectSubscriptionDto[];
}

export const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Maps OIDC user profile to the app's UserInfo type.
 * Note: preferred_username is no longer sent by the IDP (usernames are
 * internal GUIDs). The public username comes from the API entitlements.
 */
function mapOidcUserToUserInfo(
  profile: Record<string, unknown> | undefined,
): UserInfo | null {
  if (!profile) return null;

  const sub = profile.sub as string | undefined;
  const email = profile.email as string | undefined;
  const displayName = (profile.name as string | undefined) ?? email;
  if (!sub || !email) return null;

  return {
    id: sub,
    email,
    userName: email, // Placeholder — real username comes from API profile
    displayName: displayName ?? email,
    subjectId: sub,
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const apiFetch = useApiFetch();

  // Map the OIDC user to our UserInfo type
  const user = useMemo(
    () => mapOidcUserToUserInfo(auth.user?.profile),
    [auth.user?.profile],
  );

  // Track whether the user needs provisioning (404 from entitlements)
  const needsProvisioningRef = useRef(false);

  // Fetch entitlements once when authenticated.
  // A 404 means the user hasn't been provisioned yet (no API profile).
  const { data: entitlementsResponse, isLoading: isLoadingEntitlements } =
    useQuery<SubjectEntitlementsDto>({
      queryKey: entitlementsQueryKeys.all,
      queryFn: async () => {
        const result = await apiFetch<SubjectEntitlementsDto>(
          "/api/account/entitlements",
        );
        if (!result.completed) {
          throw new Error(result.error ?? "Failed to load entitlements");
        }
        // 404 = user not provisioned
        if (result.response.status === 404) {
          needsProvisioningRef.current = true;
        return { user: null as unknown as SubjectEntitlementsDto["user"], entitlements: [], system: { roles: [] }, subscriptions: [] };
        }
        if (!result.response.success) {
          throw new Error(result.response.errors?.[0] ?? "Failed to load entitlements");
        }
        needsProvisioningRef.current = false;
        return result.response.data ?? { user: null as unknown as SubjectEntitlementsDto["user"], entitlements: [], system: { roles: [] }, subscriptions: [] };
      },
      enabled: auth.isAuthenticated,
      staleTime: 5 * 60 * 1000,
    });

  const needsProvisioning = auth.isAuthenticated && !isLoadingEntitlements && needsProvisioningRef.current;

  // Redirect to Getting Started when provisioning is needed
  useEffect(() => {
    if (needsProvisioning && window.location.pathname !== AccountRoutes.GettingStarted) {
      window.location.href = AccountRoutes.GettingStarted;
    }
  }, [needsProvisioning]);

  const entitlements = entitlementsResponse?.entitlements ?? [];
  const elementRoles = useMemo(
    () => (entitlementsResponse?.system?.roles ?? []).filter((r) => r.code.startsWith("ELEMENT_")),
    [entitlementsResponse?.system?.roles],
  );
  const subscriptions = entitlementsResponse?.subscriptions ?? [];

  // Enrich user info with the profile returned by the API entitlements
  const enrichedUser = useMemo(() => {
    if (!user) return null;
    const apiProfile = entitlementsResponse?.user;
    if (!apiProfile) return user;
    return {
      ...user,
      userName: apiProfile.username,
      displayName: apiProfile.name,
    };
  }, [user, entitlementsResponse?.user]);

  const login = useCallback(
    async (returnUrl?: string) => {
      await auth.login(returnUrl);
    },
    [auth.login],
  );

  const logout = useCallback(async () => {
    await auth.logout();
  }, [auth.logout]);

  // setUser is a no-op - user state is managed by OIDC
  const setUser = useCallback((_user: UserInfo | null) => {
    // User state is managed by the OIDC provider, not locally
    // This is kept for interface compatibility
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      user: enrichedUser,
      isLoading: auth.isLoading || isLoadingEntitlements,
      isAuthenticated: auth.isAuthenticated,
      needsProvisioning,
      login,
      logout,
      setUser,
      entitlements,
      elementRoles,
      subscriptions,
    }),
    [
      enrichedUser,
      auth.isLoading,
      auth.isAuthenticated,
      needsProvisioning,
      login,
      logout,
      setUser,
      entitlements,
      elementRoles,
      subscriptions,
      isLoadingEntitlements,
    ],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
